import React, { useCallback, useContext, useMemo } from 'react'

import { ampli } from '@Analytics/ampli'
import { AdyenContext2 } from '@Domains/Checkout/Providers/AdyenContextProvider'
import { CheckoutModalContext } from '@Domains/Checkout/Providers/CheckoutModalProvider'
import { PaymentError } from '@Domains/Checkout/Steps/Payment/Components/PaymentError/PaymentError'
import { PaymentResponseCallback } from '@Domains/CheckoutV2/Features/CheckoutSteps/Contexts/typings/CheckoutFlowTypes'
import {
  getAdyenRefusalReasonCodeString,
} from '@Domains/CheckoutV2/Features/CheckoutSteps/Steps/CheckoutPaymentStep/Components/ProviderComponents/Adyen/utils/adyen/AdyenRefusalReasonCodes'
import {
  useDefaultCheckoutResponse,
} from '@Domains/CheckoutV2/Features/CheckoutSteps/Steps/CheckoutPaymentStep/Components/ProviderComponents/Default/hooks/useDefaultCheckoutResponse'
import { useDetailsForPaymentMutation } from '@Generated/graphql'
import { MetadataContext } from '@Globals/Metadata'
import { useTranslation } from '@Globals/Translations'
import { CheckoutV2FinalizeCartResult } from '@Pages/onestepcheckout'
import { AdyenPresentToShopperContent } from '@PaymentComponents/Adyen/Components/PresentToShopper/AdyenPresentToShopper'

export const useAdyenCheckoutResponse = () => {
  const { storeURL } = useContext(MetadataContext)
  const { adyenInstance } = useContext(AdyenContext2)
  const { setModal, unSetModal } = useContext(CheckoutModalContext)
  const [addDetails] = useDetailsForPaymentMutation({
    context: {
      domainProxy: true,
      userSpecific: true,
    },
  })
  const { handleErrorResponse, handleRedirect } = useDefaultCheckoutResponse()
  const { t } = useTranslation()

  const hasAdyenError = useCallback((finalizeCart: CheckoutV2FinalizeCartResult) => {
    const response = finalizeCart.paymentPayload as any
    if (response?.resultCode) {
      return response.resultCode.toLowerCase() === 'error' || response.resultCode.toLowerCase() === 'refused'
    }
    return !finalizeCart.success
  }, [])

  const handleAdyenError = useCallback((finalizeCart: CheckoutV2FinalizeCartResult, paymentType: string) => {
    if (hasAdyenError(finalizeCart)) {
      ampli.cofCompletionFailRes({
        payment_method: paymentType,
      })
      const response = finalizeCart.paymentPayload as any
      setModal(<PaymentError closeModal={unSetModal} error={t(getAdyenRefusalReasonCodeString(response.refusalReasonCode))} />)
      return true
    }
    return false
  }, [hasAdyenError, setModal, t, unSetModal])

  const handleAdyenRedirectFromAction = useCallback((finalizeCart: CheckoutV2FinalizeCartResult) => {
    const response = finalizeCart.paymentPayload as any
    const validPendingTypes = ['redirect'].includes(response?.action?.type)
    const pendingWithType = response?.resultCode?.toUpperCase() === 'PENDING'
    const redirectShopper = (pendingWithType && validPendingTypes) || response?.resultCode?.toUpperCase() === 'REDIRECTSHOPPER'

    if (redirectShopper && response?.action) {
      adyenInstance.createFromAction(response.action).mount('#adyen-payment-result-container')
      return true
    }
    return false
  }, [adyenInstance])

  const submitAdyenAdditionalDetails = useCallback(async (data) => {
    try {
      const response = await addDetails({
        variables: {
          payload: data,
        },
      })
      if (response.data?.getDetailsForPayment?.success && response.data?.getDetailsForPayment?.redirect) {
        window.location.href = storeURL('checkout/onepage/success')
        return
      }
      console.error('submitAdyenAdditionalDetails - Details - Status OK but no redirect')
      setModal(<PaymentError closeModal={unSetModal} error={t('common:SOMETHING_WENT_WRONG_PLEASE_CONTACT_US')} />)
    } catch (e) {
      console.error('submitAdyenAdditionalDetails - Details - Exception', e)
      setModal(<PaymentError closeModal={unSetModal} error={t('common:SOMETHING_WENT_WRONG_PLEASE_CONTACT_US')} />)
    }
  }, [addDetails, setModal, storeURL, t, unSetModal])

  const handleAdyenPresentToShopperFromAction = useCallback((finalizeCart: CheckoutV2FinalizeCartResult) => {
    const response = finalizeCart.paymentPayload as any
    const validPendingTypes = ['qrCode', 'await'].includes(response?.action?.type)
    const pendingWithType = response?.resultCode?.toUpperCase() === 'PENDING'
    const presentToShopper = (pendingWithType && validPendingTypes) || response?.resultCode?.toUpperCase() === 'PRESENTTOSHOPPER'
    if (presentToShopper && response?.action) {
      setModal(<AdyenPresentToShopperContent result={response} />)
      const createFormAction = () => {
        adyenInstance.createFromAction(response.action, {
          onAdditionalDetails: (state) => {
            submitAdyenAdditionalDetails(state.data)
          },
        }).mount('#adyen-payment-result-modal-container')
      }
      document.addEventListener('AdyenPresentToShopperModalOpened', () => {
        createFormAction()
        document.removeEventListener('AdyenPresentToShopperModalOpened', createFormAction)
      })
      return true
    }
    return false
  }, [adyenInstance, setModal, submitAdyenAdditionalDetails])

  const handleAdyenResponse: PaymentResponseCallback = useCallback((paymentType: string, finalizeCart: CheckoutV2FinalizeCartResult) => {
    let res: boolean = handleAdyenError(finalizeCart, paymentType)
    res = res || handleAdyenRedirectFromAction(finalizeCart)
    res = res || handleAdyenPresentToShopperFromAction(finalizeCart)
    res = res || handleErrorResponse(finalizeCart, paymentType)
    res = res || handleRedirect(finalizeCart)
    return res
  }, [handleAdyenPresentToShopperFromAction, handleAdyenRedirectFromAction, handleAdyenError, handleErrorResponse, handleRedirect])

  const value = useMemo(() => ({ handleAdyenResponse, submitAdyenAdditionalDetails }), [handleAdyenResponse, submitAdyenAdditionalDetails])

  return value
}
